import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products } from '../data/products';
import { ProductCard } from '../components/ProductCard';
interface ContactFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}
const emptyContact: ContactFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: ''
};
export function Home() {
  const featuredProducts = products.slice(0, 4);
  const [form, setForm] = useState<ContactFormState>(emptyContact);
  const [smsAutomated, setSmsAutomated] = useState(false);
  const [smsPromotional, setSmsPromotional] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormState, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const setField = (key: keyof ContactFormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const isValidPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11 && digits.startsWith('1');
  };
  const validate = (): boolean => {
    const next: Partial<Record<keyof ContactFormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = 'Required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email.';
    if (!isValidPhone(form.phone)) next.phone = 'Enter a valid US phone number.';
    if (!form.message.trim()) next.message = 'Required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
  };
  const inputClass = (key: keyof ContactFormState) => `w-full bg-neutral-900 border px-4 py-3 text-white focus:outline-none rounded-sm transition-colors ${errors[key] ? 'border-red-500/70 focus:border-red-500' : 'border-neutral-700 focus:border-vsc-gold'}`;
  return <div className="flex flex-col" data-id="element-216">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 px-4 overflow-hidden" data-id="element-217">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" data-id="element-218">
          <span className="font-display text-[35vw] leading-none text-stroke-white select-none" data-id="element-219">
            VSC
          </span>
        </div>
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} className="relative z-10 w-full max-w-6xl aspect-[4/5] sm:aspect-square md:aspect-[21/9] bg-neutral-900 overflow-hidden rounded-sm" data-id="element-220">
          <img src="https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?auto=format&fit=crop&q=80&w=2000" alt="VSC Lifestyle" className="w-full h-full object-cover opacity-60" data-id="element-221" />
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-222">
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-wide text-white uppercase drop-shadow-lg" data-id="element-223">
              Wear the Culture.
            </h1>
            <p className="font-sans text-lg md:text-2xl text-neutral-200 mt-6 max-w-2xl drop-shadow-md font-light" data-id="element-224">
              Premium streetwear. Louisville made. Globally worn.
            </p>
            <Link to="/shop" className="mt-10 bg-vsc-gold text-black font-medium px-8 py-4 hover:bg-white transition-colors rounded-sm text-lg" data-id="element-225">
              Shop Collection
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-4 max-w-7xl mx-auto w-full" data-id="element-226">
        <div className="flex justify-between items-end mb-12" data-id="element-227">
          <h2 className="font-display text-4xl md:text-5xl tracking-wide" data-id="element-228">
            Latest Drops
          </h2>
          <Link to="/shop" className="text-vsc-gold hover:text-white transition-colors font-medium border-b border-vsc-gold hover:border-white pb-1" data-id="element-229">
            View All
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-230">
          {featuredProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-231" />)}
        </div>
      </section>

      {/* Contact / Opt-in Section */}
      <section id="contact" className="py-24 px-4 bg-neutral-900/30 border-y border-neutral-800" data-id="element-232">
        <div className="max-w-2xl mx-auto w-full" data-id="element-233">
          <div className="text-center mb-10" data-id="element-234">
            <h2 className="font-display text-4xl md:text-5xl mb-4 tracking-wide" data-id="element-235">
              Contact Us
            </h2>
            <p className="text-neutral-400 max-w-xl mx-auto" data-id="element-236">
              Questions about an order, sizing, or anything else? Send us a
              message and opt in to stay in the loop.
            </p>
          </div>

          {submitted ? <div className="bg-neutral-900/50 border border-vsc-gold/40 rounded-sm p-8 text-center" data-id="element-237">
              <p className="font-display text-3xl tracking-wide text-vsc-gold mb-2" data-id="element-238">
                Thanks — message received
              </p>
              <p className="text-neutral-300" data-id="element-239">
                We'll get back to you soon. If you opted in to text messages,
                reply STOP at any time to unsubscribe or HELP for help.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-240">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-241">
                <div data-id="element-242">
                  <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-243">
                    First name *
                  </label>
                  <input value={form.firstName} onChange={e => setField('firstName', e.target.value)} className={inputClass('firstName')} aria-invalid={!!errors.firstName} data-id="element-244" />
                  {errors.firstName && <p className="text-red-400 text-xs mt-1" data-id="element-245">
                      {errors.firstName}
                    </p>}
                </div>
                <div data-id="element-246">
                  <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-247">
                    Last name
                  </label>
                  <input value={form.lastName} onChange={e => setField('lastName', e.target.value)} className={inputClass('lastName')} data-id="element-248" />
                </div>
              </div>

              <div data-id="element-249">
                <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-250">
                  Email *
                </label>
                <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} className={inputClass('email')} aria-invalid={!!errors.email} data-id="element-251" />
                {errors.email && <p className="text-red-400 text-xs mt-1" data-id="element-252">{errors.email}</p>}
              </div>

              <div data-id="element-253">
                <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-254">
                  Phone *
                </label>
                <input type="tel" placeholder="(555) 555-5555" value={form.phone} onChange={e => setField('phone', e.target.value)} className={inputClass('phone')} aria-invalid={!!errors.phone} data-id="element-255" />
                {errors.phone && <p className="text-red-400 text-xs mt-1" data-id="element-256">{errors.phone}</p>}
              </div>

              <div data-id="element-257">
                <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-258">
                  Message *
                </label>
                <textarea rows={4} value={form.message} onChange={e => setField('message', e.target.value)} className={`${inputClass('message')} resize-y`} aria-invalid={!!errors.message} data-id="element-259" />
                {errors.message && <p className="text-red-400 text-xs mt-1" data-id="element-260">{errors.message}</p>}
              </div>

              {/* Consent — checkboxes unchecked by default, never required to submit */}
              <div className="space-y-4 pt-2" data-id="element-261">
                <label className="flex gap-3 text-left cursor-pointer" data-id="element-262">
                  <input type="checkbox" checked={smsAutomated} onChange={e => setSmsAutomated(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-263" />
                  <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-264">
                    By checking this box, you consent to receive{' '}
                    <strong className="text-white" data-id="element-265">
                      automated follow-up and reminder
                    </strong>{' '}
                    SMS messages from VSCCLOTHING d/b/a VSC Clothing with
                    varying frequency. Msg and data rates may apply. Reply STOP
                    to end. Text HELP for help. See{' '}
                    <Link to="/sms-terms" className="underline hover:text-white" data-id="element-266">
                      SMS Terms
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline hover:text-white" data-id="element-267">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>

                <label className="flex gap-3 text-left cursor-pointer" data-id="element-268">
                  <input type="checkbox" checked={smsPromotional} onChange={e => setSmsPromotional(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-269" />
                  <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-270">
                    By checking this box, you consent to receive{' '}
                    <strong className="text-white" data-id="element-271">promotional</strong> SMS
                    messages from VSCCLOTHING d/b/a VSC Clothing with varying
                    frequency. Msg and data rates may apply. Reply STOP to end.
                    Text HELP for help. See{' '}
                    <Link to="/sms-terms" className="underline hover:text-white" data-id="element-272">
                      SMS Terms
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline hover:text-white" data-id="element-273">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>

                <label className="flex gap-3 text-left cursor-pointer" data-id="element-274">
                  <input type="checkbox" checked={newsletter} onChange={e => setNewsletter(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-275" />
                  <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-276">
                    Yes, subscribe me to your newsletter.
                  </span>
                </label>
              </div>

              <button type="submit" className="w-full bg-vsc-gold text-black font-medium py-4 text-lg hover:bg-white transition-colors rounded-sm" data-id="element-277">
                Submit
              </button>
            </form>}
        </div>
      </section>
    </div>;
}